/**
 * Collection view — the shared archetype for "a set of things you gather".
 *
 * `docs/systemsRework/LayerNavigation.md` flags this as a pattern that recurs
 * across the ladder (artifacts, Legends, Blackout perks, Collapse Keys, L4's
 * traced hubs) and asks for it to be extracted on the SECOND instance rather
 * than the fifth. This is that extraction, built for the Archive first.
 *
 * It is a PURE STRING BUILDER — no DOM, no state, no event wiring — so it can
 * be asserted headlessly and reused by any screen that owns its own container.
 *
 * Two rules the archetype encodes, because both are easy to get wrong once per
 * screen otherwise:
 *
 *   1. LOCKED SLOTS ARE SHOWN. A collection that hides what you have not found
 *      cannot tell you that anything is missing, and the shape of the set is
 *      most of what makes collecting one interesting.
 *   2. FOUND ENTRIES STAY READABLE. Everything is a <details>, so an entry can
 *      be re-opened forever with no JS state to lose. That is the whole reason
 *      the Archive exists rather than a toast: `docs/archive/BreachLayer.md`
 *      records that story previously had nowhere to live.
 *
 * @typedef {Object} CollectionEntry
 * @property {string}  id        - stable id, used for the DOM id
 * @property {boolean} owned     - found, or a locked slot
 * @property {string}  title     - shown on the card face when owned
 * @property {string} [subtitle] - small line under the title
 * @property {string} [icon]     - leading glyph
 * @property {string} [marker]   - short designation shown on locked slots too
 * @property {Array<{label?: string, text: string, quote?: boolean}>} [sections]
 *                               - the body, revealed on open
 *
 * @typedef {Object} CollectionGroup
 * @property {string}  id
 * @property {string}  label
 * @property {string} [icon]
 * @property {string} [accent]   - CSS colour for this group's rules and counts
 * @property {string} [blurb]    - one line under the group heading
 * @property {Array<CollectionEntry>} entries
 */

const escapeHtml = (s) => String(s ?? '')
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;');

const DEFAULT_ACCENT = '#9ca3af';

function entryHtml(entry, accent) {
  const marker = entry.marker ? escapeHtml(entry.marker) : '';

  if (!entry.owned) {
    // A locked slot still carries its designation. Knowing that D3 exists and
    // is missing is the information a collection is FOR.
    return `
      <div class="collection-slot locked rounded-lg border border-dashed border-gray-700 bg-white/[0.02] p-3 text-center">
        <div class="text-xl leading-none opacity-40">🔒</div>
        ${marker ? `<div class="text-[11px] font-bold tracking-widest mt-1 text-gray-500">${marker}</div>` : ''}
        <div class="text-[11px] text-gray-600 mt-1">Not recovered</div>
      </div>`;
  }

  const body = (entry.sections || []).map(section => {
    const label = section.label
      ? `<div class="uppercase tracking-widest text-[10px] font-bold mb-1" style="color:${accent}">${escapeHtml(section.label)}</div>`
      : '';
    const text = section.quote
      ? `<div class="text-sm italic leading-relaxed border-l-2 pl-3 py-1" style="border-color:${accent}; color:#d6cdf0;">${escapeHtml(section.text)}</div>`
      : `<div class="text-sm leading-relaxed text-gray-300">${escapeHtml(section.text)}</div>`;
    return `<div class="mb-3">${label}${text}</div>`;
  }).join('');

  return `
    <details id="collection-${escapeHtml(entry.id)}" class="collection-slot rounded-lg border bg-white/5 p-3 h-full" style="border-color:${accent}66;">
      <summary class="cursor-pointer list-none">
        <div class="flex items-baseline gap-2">
          ${entry.icon ? `<span class="text-lg leading-none">${entry.icon}</span>` : ''}
          <span class="font-bold text-white text-sm">${escapeHtml(entry.title)}</span>
          ${marker ? `<span class="text-[10px] font-bold tracking-widest ml-auto" style="color:${accent}">${marker}</span>` : ''}
        </div>
        ${entry.subtitle ? `<div class="text-[11px] text-gray-400 mt-1">${escapeHtml(entry.subtitle)}</div>` : ''}
      </summary>
      <div class="mt-3 pt-3 border-t border-gray-700">${body}</div>
    </details>`;
}

function groupHtml(group) {
  const accent = group.accent || DEFAULT_ACCENT;
  const found = group.entries.filter(e => e.owned).length;
  const total = group.entries.length;

  return `
    <section class="mb-6">
      <div class="flex items-baseline gap-2 border-b pb-1 mb-2" style="border-color:${accent}55;">
        <h3 class="font-bold" style="color:${accent}">${group.icon || ''} ${escapeHtml(group.label)}</h3>
        <span class="text-xs ml-auto" style="color:${accent}">${found}/${total}</span>
      </div>
      ${group.blurb ? `<p class="text-xs text-gray-400 mb-3">${escapeHtml(group.blurb)}</p>` : ''}
      <div class="grid gap-2 collection-grid">
        ${group.entries.map(e => entryHtml(e, accent)).join('')}
      </div>
    </section>`;
}

/**
 * Render a collection to HTML.
 *
 * @param {{groups: Array<CollectionGroup>, empty?: string}} spec
 * @returns {string}
 */
export function collectionHtml(spec) {
  const groups = spec?.groups || [];
  const populated = groups.filter(g => (g.entries || []).length > 0);

  if (!populated.length) {
    return `<div class="text-center text-gray-400 py-8 border border-dashed border-gray-600 rounded-lg">
      ${escapeHtml(spec?.empty || 'Nothing collected yet.')}
    </div>`;
  }

  return populated.map(groupHtml).join('');
}
